// noinspection TypeScriptValidateJSTypes

import { AccessLevel, merchantAdminApi, ProductDetailDto } from './merchantAdminApi';
import { enhancedInventoryApi } from './enhancedInventoryApi';
import ProductUtilities from "../features/products/product-utilities";

export const enhancedMerchantAdminApi = merchantAdminApi.enhanceEndpoints({
    addTagTypes: [
        "MerchantUser", "MerchantContact", "Product", "ProductCategory", "MerchantLocation", "UserContext", "UserNotification",
    ],
    endpoints: {
        merchantsControllerRetrieveMerchant: { providesTags: ["Merchant"] },
        merchantsControllerUpdateMerchant: { invalidatesTags: ["Merchant"] },
        merchantsControllerUpdateMerchantConfiguration: { invalidatesTags: ["Merchant"] },
        merchantsControllerConfigureScanResultExport: { invalidatesTags: ["Merchant"] },
        merchantsControllerDisableScanResultExport: { invalidatesTags: ["Merchant"] },
        merchantsControllerConfigureScanResultExportForProfile: {
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(enhancedInventoryApi.util.invalidateTags(["ConfigurationProfile"]));
            },
        },
        merchantsControllerDisableScanResultExportForProfile: {
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(enhancedInventoryApi.util.invalidateTags(["ConfigurationProfile"]));
            },
        },

        merchantUsersControllerRetrieveMerchantUsers: { providesTags: ["MerchantUser"] },
        merchantUsersControllerCreateMerchantUser: { invalidatesTags: ["MerchantUser"] },
        merchantUsersControllerUpdateMerchantUser: { invalidatesTags: ["MerchantUser"] },

        merchantUsersControllerRetrieveUserAuthenticatorsSelfManageUrl: {
            query: (queryArg) => ({
                url: `/merchants/${queryArg.merchantId}/users/${queryArg.userId}/authenticators-self-manage-url`,
                params: { redirectUrl: queryArg.redirectUrl },
                responseHandler: (response) => response.text(),
            }),
        },
        merchantUsersControllerResetUserAuthenticators: {},
        userContextControllerRetrieveUserContext: { providesTags: ["UserContext"] },

        productsControllerRetrieveProducts: { providesTags: ["Product"] },
        productsControllerRetrieveMerchantProduct:
            {
                providesTags: ["Product"],
                transformResponse: (response: ProductDetailDto) => {
                    if (response?.sizing?.fits) {
                        // Thresholds get deserialised into an array so we convert it to a dictionary
                        response.sizing.fits.forEach((fit) => {
                            if (Array.isArray(fit.thresholds)) {
                                fit.thresholds = fit.thresholds.reduce((acc: { [key: string]: number }, item: {
                                    key: string;
                                    value: number
                                }) => {
                                    acc[item.key] = item.value;
                                    return acc;
                                }, {});
                            }
                        });
                        // We also remove offsets from the size chart values
                        response.sizing = ProductUtilities.RemoveOffsetsFromSizeChart(response.sizing);
                    }
                    return response;
                },
            },
        productsControllerRetrieveSizeChartCsv: {
            providesTags: ["Product"],
            query: (queryArg) => ({
                url: `/products/${queryArg.merchantId}/${queryArg.productCode}/chart`,
                responseHandler: (response) => response.text(),
            }),
        },
        productsControllerCreateProduct: { invalidatesTags: ["Product"] },
        productsControllerUpdateProduct: { invalidatesTags: ["Product"] },
        productsControllerUpdateSizeChart: { invalidatesTags: ["Product"] },

        productCategoriesControllerRetrieveCategories: { providesTags: ["ProductCategory"] },
        productCategoriesControllerCreateProductCategory: { invalidatesTags: ["ProductCategory"] },
        productCategoriesControllerDeleteProductCategory: { invalidatesTags: ["ProductCategory"] },

        productsControllerRetrieveBulkProducts: {
            providesTags: ["Product"],
            query: (queryArg) => ({
                url: `/products/${queryArg.merchantId}/bulk`,
                params: { example: queryArg.example },
                responseHandler: (response) => response.text(),
            }),
        },
        productsControllerBulkProductUpdate: {
            invalidatesTags: ["Product"],
            query: (queryArg) => ({
                url: `/products/${queryArg.merchantId}/bulk`,
                method: "PUT",
                body: queryArg.body,
                responseHandler: (response) => response.text(),
            }),
        },
        productsControllerBulkProductImport: {
            invalidatesTags: ["Product"],
            query: (queryArg) => ({
                url: `/products/${queryArg.merchantId}/bulk`,
                method: "POST",
                body: queryArg.body,
                responseHandler: (response) => response.text(),
            }),
        },
        productFunctionalFitControllerCompleteAttachmentUpload: { invalidatesTags: ["Product"] },
        productFunctionalFitControllerCreateFitExample: { invalidatesTags: ["Product"] },
        productFunctionalFitControllerDeleteAttachment: { invalidatesTags: ["Product"] },
        productFunctionalFitControllerDeleteFitExample: { invalidatesTags: ["Product"] },
        productFunctionalFitControllerUpdateProductFitAssessment: { invalidatesTags: ["Product"] },
        productsControllerDeleteProduct: { invalidatesTags: ["Product"] },
        reportsControllerRetrieveProductSizingReport: {},
        systemControllerCreateUserNotification: { invalidatesTags: ["UserNotification", "UserContext"] },
        systemControllerDeleteUserNotification: { invalidatesTags: ["UserNotification", "UserContext"] },
        systemControllerRetrieveUserNotifications: { providesTags: ["UserNotification"] },
    },
});

type ValueOf<T> = T[keyof T];
type NonEmptyArray<T> = [T, ...T[]]
type MustInclude<T, U extends T[]> = [T] extends [ValueOf<U>] ? U : never;

function stringUnionToArray<T>() {
    return <U extends NonEmptyArray<T>>(...elements: MustInclude<T, U>) => elements;
}

export const accessLevelMap: { [key in AccessLevel]: string } = {
    'Merchant': 'Merchant',
    'MerchantAdmin': 'Merchant Admin',
    'Bodd': 'Bodd',
    'BoddLimited': 'Bodd Limited',
    'QualityAssurance': 'Quality Assurance',
    'None': 'None',
    'MerchantOwner': 'Merchant Owner',
    'SystemOwner': 'System Owner',
}

export const allowedProductImageFileTypes = ['.jpg', '.png'];
export const maxProductImageSize = 1000000;
export const maxProductImportFileSize = 1000000;
export const maxSizeChartFileSize = 1000000;
export const maxProductAttachmentFileSize = 150_000_000;


export * from './merchantAdminApi'

export const {
    useMerchantsControllerRetrieveMerchantsQuery,
    useProductsControllerRetrieveProductsQuery,
    useProductCategoriesControllerRetrieveCategoriesQuery,
    useMerchantsControllerCreateMerchantMutation,
    useMerchantsControllerRetrieveMerchantQuery,
    useMerchantsControllerUpdateMerchantMutation,
    useProductsControllerCreateProductMutation,

    useLazyMerchantUsersControllerRetrieveUserAuthenticatorsSelfManageUrlQuery,
    useMerchantUsersControllerResetUserAuthenticatorsMutation,

    useMerchantUsersControllerRetrieveMerchantUsersQuery,
    useMerchantUsersControllerCreateMerchantUserMutation,
    useMerchantUsersControllerUpdateMerchantUserMutation,
    useProductsControllerUpdateProductMutation,
    useProductCategoriesControllerDeleteProductCategoryMutation,
    useProductsControllerRetrieveMerchantProductQuery,
    useProductCategoriesControllerCreateProductCategoryMutation,
    useProductsControllerBulkProductImportMutation,
    useUserContextControllerRetrieveUserContextQuery,
    useProductFunctionalFitControllerUpdateProductFitAssessmentMutation,
    useProductsControllerUpdateSizeChartMutation,
    useLazyProductsControllerRetrieveBulkProductsQuery,
    useProductsControllerBulkProductUpdateMutation,
    useLazyProductsControllerRetrieveSizeChartCsvQuery,
    useLazyUserContextControllerRetrieveUserContextQuery,

    useProductFunctionalFitControllerInitiateAttachmentUploadMutation,
    useProductFunctionalFitControllerCompleteAttachmentUploadMutation,

    useProductFunctionalFitControllerDeleteAttachmentMutation,
    useProductFunctionalFitControllerCreateFitExampleMutation,
    useProductFunctionalFitControllerDeleteFitExampleMutation,

    useReportsControllerRetrieveProductSizingReportQuery,
    useMerchantsControllerConfigureScanResultExportForProfileMutation,
    useMerchantsControllerDisableScanResultExportForProfileMutation,
} = enhancedMerchantAdminApi;


